<template>
  <div class="ag-cell-group-pill-renderer" data-test-id="group-pill-renderer">
    <div
      ref="ScrollContainer"
      :class="{
        'ag-cell-group-pill-renderer__container': true,
        'ag-cell-group-pill-renderer__wrapped-column': isColumnWrapped,
      }"
    >
      <GroupPill v-for="item in groups" :key="item.id" :item="item" show-full-pill-name />
      <Btn
        v-if="!projectEntityGroups.length"
        data-test-id="group-pill-renderer-manage-groups-button"
        color="grey"
        @click="handleAddGroupClick"
      >
        {{ $t('gridList.renderers.groupPillRenderer.manageGroups') }}
        <AddIcon16 />
      </Btn>

      <transition name="fade-transition">
        <span v-if="showGradient" class="ag-cell__gradient"></span>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import {
  Group,
  ProjectGroupsDocument,
  ProjectGroupsQuery,
  ProjectGroupsQueryVariables,
} from '@/graphql/operations.gen';
import AddIcon16 from '@components/Icons/AddIcon16.vue';
import { ICellRendererParams } from 'ag-grid-community';
import {
  ProjectCode,
  ProjectCodeGroup,
  ProjectMemo,
  ProjectMemoGroup,
  ProjectSource,
  ProjectSourceGroup,
} from '@/types';
import { VueApolloQueryDefinition } from '@vue/apollo-option/types/options';

export default defineComponent({
  name: 'GroupPillRenderer',
  components: { AddIcon16 },
  props: {
    params: {
      type: Object as PropType<{ projectId: string } & ICellRendererParams<ProjectCode | ProjectMemo | ProjectSource>>,
      required: true,
    },
  },

  data(): {
    showGradient: boolean;
    resizeObserver: ResizeObserver | null;
    project: ProjectGroupsQuery['project'];
    isColumnWrapped: boolean;
    colId: string;
  } {
    return {
      showGradient: true,
      resizeObserver: null,
      project: undefined,
      isColumnWrapped: true,
      colId: '',
    };
  },

  apollo: {
    project(): VueApolloQueryDefinition<ProjectGroupsQuery, ProjectGroupsQueryVariables> {
      return {
        query: ProjectGroupsDocument,
        fetchPolicy: 'cache-only',
        variables: () => ({
          id: this.params.projectId,
        }),
      };
    },
  },

  created() {
    this.colId = this.params.column.getColId();
    this.$watch(
      () => this.$store.state.gridWrap.wrappedColumns[this.colId],
      () => {
        this.isColumnWrapped = this.$store.getters['gridWrap/isColumnWrapped'](this.colId);
        // this.params.api.refreshCells();
        // this.refreshCell();
      },
      { immediate: true }
    );
  },

  mounted() {
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.$refs.ScrollContainer.addEventListener('scroll', this.handleScroll);
    this.resizeObserver.observe(this.$refs.ScrollContainer);
  },

  beforeUnmount() {
    if (!this.$refs.ScrollContainer) return;
    this.$refs.ScrollContainer.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver.unobserve(this.$refs.ScrollContainer);
  },

  methods: {
    handleResize() {
      const scrollContainer = this.$refs.ScrollContainer as HTMLElement;

      if (scrollContainer.scrollWidth !== scrollContainer.clientWidth && scrollContainer.scrollLeft === 0) {
        this.showGradient = true;

        return;
      }

      this.showGradient = false;
    },
    handleScroll(event): void {
      this.showGradient = !Boolean(event.target.scrollLeft);
    },
    handleAddGroupClick() {
      this.$store.dispatch('modal/show', { type: this.editGroupModalType, label: 'secondary button' });
    },
  },

  computed: {
    entityType(): 'source' | 'memo' | 'code' {
      return this.params.data.__typename.toLowerCase() as 'source' | 'memo' | 'code';
    },
    editGroupModalType(): string {
      return `${this.entityType}EditGroups`;
    },
    groups(): Array<Group> {
      // removes groups that have been deleted from the project
      return this.params.data.groups.flatMap(
        (group) => this.projectEntityGroups.find((projectGroup) => projectGroup.id === group.id) || []
      );
    },
    projectEntityGroups(): Array<ProjectCodeGroup | ProjectMemoGroup | ProjectSourceGroup> {
      return this.project?.[`${this.entityType}Groups`] || [];
    },
  },
});
</script>

<style lang="scss" scoped>
@use 'mixins/base-renderer';

.ag-cell-group-pill-renderer {
  @include base-renderer.base-renderer;
  height: 100%;
  display: flex;
  position: relative;
}
.ag-cell-group-pill-renderer__container {
  padding: 14px 0;
  width: calc(100% - 4px);
  height: 100%;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
  overflow: auto;

  // hide scrollbar: could lead to compatibility issues

  // IE & Edge
  -ms-overflow-style: none;
  // Firefox
  scrollbar-width: none;
  // Webkit
  &::-webkit-scrollbar {
    display: none;
  }
}
.ag-cell-group-pill-renderer__wrapped-column {
  overflow: hidden;
  flex-wrap: wrap;
}
</style>
